import {
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query'
import {
    LocationParams,
    SCHOOLS_API,
    TeacherSimpleData,
    fetchBookableTeacherForDate,
    fetchTeacherProfile,
    fetchTeacherSimpleData,
} from '../../../firebase/school-api'
import {
    BookingConfig,
    SchoolBookingKeyEntity,
} from '../../../redux/schoolBookingsSlice'
import dayjs, { Dayjs } from 'dayjs'
import {
    BookingReplacementState,
    BookingSchoolNotes,
    SchoolBooking,
    schoolBookingKeyNames,
    SchoolBookingType,
} from '../../../firebase/types-bookings'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BookableTeachersForDate } from '../../../firebase/types-school'
import { BOOKINGS_API } from '../../../firebase/bookings-api'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { BookingTableData } from './BookingsPage/helper'
import { TeacherPublicInfo } from '../../../firebase/types-teacher'
import { MoreTeacherRow } from './BookingsPage/BookingDateComponents/BookableTeachersTable'
import { useSelector } from 'react-redux'
import {
    selectSchoolConfig,
    selectSchoolCoordinates,
    selectSchoolFavourites,
    selectSchoolUid,
} from '../../../redux/schoolSlice'
import { whichEmployentSetting } from '../helper'
import {
    LocalTeacherEmploymentSetting,
    TeacherEmploymentSetting,
} from '../../../redux/types'
import { mToMs, sToMs } from '../../../helpers/utils'
import { selectBookingDetails } from '../../../redux/schoolModalSlice'
import { JSONEqual } from '../../../redux/helpers'
import { distanceBetween } from 'geofire-common'
import { logger } from '../../../helpers/logger'
dayjs.extend(customParseFormat)

// Teacher Data used in tables
export const useTeacherSimple = ({
    teacherUid,
    completionRate,
}: {
    teacherUid?: string
    completionRate?: boolean
}) => {
    const schoolConfig = useSelector(selectSchoolConfig)

    return useQuery<TeacherSimpleData | null>({
        queryKey: ['bookings', 'teacherData', teacherUid],
        queryFn: async () =>
            schoolConfig.loaded && !!teacherUid
                ? fetchTeacherSimpleData(
                      schoolConfig.data.country,
                      schoolConfig.data.locationState,
                      teacherUid,
                      completionRate
                  )
                : null,
        enabled: !!teacherUid && schoolConfig.loaded,
        staleTime: mToMs(15), // NOTE: Set to 10 mins -> How often would this data change?
        gcTime: mToMs(20),
        refetchOnMount: false,
    })
}

/**
 * Teacher Data used for their profile (includes all public info data)
 */
export const useTeacherProfile = ({ teacherUid }: { teacherUid?: string }) => {
    const schoolConfig = useSelector(selectSchoolConfig)

    return useQuery<TeacherPublicInfo | null>({
        queryKey: ['teacherProfile', teacherUid],
        queryFn: async () => {
            if (!schoolConfig.loaded || !teacherUid) return null
            // Derive teacher type

            const data = await fetchTeacherProfile(
                schoolConfig.data.country,
                schoolConfig.data.locationState,
                teacherUid
            )

            return {
                teacherUid,
                ...data,
                setting: whichEmployentSetting(
                    schoolConfig.data.locationState,
                    data.employmentSetting
                ),
            }
        },
        refetchOnWindowFocus: false,
        staleTime: mToMs(10),
        gcTime: mToMs(30),
        enabled: schoolConfig.loaded && !!teacherUid,
    })
}

export type TeacherProfileData = TeacherPublicInfo & {
    teacherUid: string
    setting: LocalTeacherEmploymentSetting
}
/**
 * useTeacherProfile for a set of teachers.
 * Returns their public info data
 */
export const useTeacherProfiles = ({
    teacherUids,
    disableQueries,
}: {
    disableQueries?: boolean
    teacherUids: string[]
}) => {
    const schoolConfig = useSelector(selectSchoolConfig)

    return useQueries({
        queries: teacherUids.map((uid) => ({
            queryKey: ['teacherProfile', uid],
            queryFn: async () => {
                if (!schoolConfig.loaded) return null

                const data = await fetchTeacherProfile(
                    schoolConfig.data.country,
                    schoolConfig.data.locationState,
                    uid
                )

                return {
                    teacherUid: uid,
                    ...data,
                    setting: whichEmployentSetting(
                        schoolConfig.data.locationState,
                        data.employmentSetting
                    ),
                } as TeacherProfileData
            },
            refetchOnWindowFocus: false,
            staleTime: mToMs(10),
            gcTime: mToMs(30),
            enabled: !Boolean(disableQueries) && schoolConfig.loaded && !!uid,
        })),
        combine: useCallback(
            (
                result: any[]
            ): {
                data: Record<string, TeacherProfileData>
                pending: boolean
            } => ({
                data: result.reduce(
                    (acc, curr) =>
                        !!curr.data
                            ? { ...acc, [curr.data.teacherUid]: curr.data }
                            : acc,
                    {} as Record<string, TeacherProfileData>
                ),
                pending: result.some((r) => r.isPending),
            }),
            []
        ),
    })
}

/**
 * Handles data for a single booking. Fetches each children of the booking node.
 * @param config BookingConfig for the booking
 * @returns object with data as the entire node reconstructed, and a pending state
 */
export const useBookingData = ({
    config,
    dateObj,
}: {
    config?: BookingConfig
    dateObj: Dayjs
}) => {
    const schoolConfig = useSelector(selectSchoolConfig)

    return useQueries({
        queries:
            !!config && schoolConfig.loaded
                ? schoolBookingKeyNames.map((el) => ({
                      queryKey: [`bookings`, el, config.placementUid],
                      queryFn: async () =>
                          ({
                              key: el,
                              data: await BOOKINGS_API.fetchChild<
                                  SchoolBooking[typeof el]
                              >(
                                  {
                                      ...config,
                                      ...schoolConfig.data,
                                      dateObj,
                                  },
                                  el
                              ),
                          }) as {
                              key: keyof SchoolBooking
                              data: SchoolBooking[typeof el]
                          },
                      staleTime: mToMs(1),
                      gcTime: mToMs(5),
                  }))
                : [],
        combine: (results) => ({
            data: results.reduce(
                (a, c) => ({
                    ...a,
                    [String(c.data?.key)]: c.data?.data || null,
                }),
                {} as SchoolBooking
            ),
            isPending: results.some((r) => r.isPending),
        }),
    })
}

export const useBookingChild = <T>(
    key: keyof SchoolBooking,
    staleTime?: number
) => {
    const schoolConfig = useSelector(selectSchoolConfig)
    const bookingConfig = useSelector(selectBookingDetails)

    return useQuery<{ key: keyof SchoolBooking; data: T | null } | null>({
        queryKey: [`bookings`, key, bookingConfig?.placementUid],
        queryFn: async () =>
            !!bookingConfig && schoolConfig.loaded
                ? ({
                      key,
                      data: await BOOKINGS_API.fetchChild<T>(
                          {
                              ...bookingConfig,
                              ...schoolConfig.data,
                              dateObj: dayjs(bookingConfig.date, 'DD-MM-YYYY'),
                          },
                          key
                      ),
                  } as {
                      key: keyof SchoolBooking
                      data: T | null
                  })
                : null,
        staleTime: staleTime || mToMs(1),
        gcTime: mToMs(5),
        enabled: !!bookingConfig && schoolConfig.loaded,
    })
}

export const useBookingChildWithArgs = <T>(
    key: keyof SchoolBooking,
    bookingConfig: BookingConfig
) => {
    const schoolConfig = useSelector(selectSchoolConfig)

    return useQuery<{ key: keyof SchoolBooking; data: T | null } | null>({
        queryKey: [`bookings`, key, bookingConfig.placementUid],
        queryFn: async () =>
            !!bookingConfig && schoolConfig.loaded
                ? ({
                      key,
                      data: await BOOKINGS_API.fetchChild<T>(
                          {
                              ...bookingConfig,
                              ...schoolConfig.data,
                              dateObj: dayjs(bookingConfig.date, 'DD-MM-YYYY'),
                          },
                          key
                      ),
                  } as {
                      key: keyof SchoolBooking
                      data: T | null
                  })
                : null,
        staleTime: mToMs(1),
        gcTime: mToMs(5),
        enabled: !!bookingConfig && schoolConfig.loaded,
    })
}
export const useBookingTeacherFirstName = () => {
    const schoolConfig = useSelector(selectSchoolConfig)
    const bookingConfig = useSelector(selectBookingDetails)

    return useQuery<string | null>({
        queryKey: [`bookings`, 'teacherName', bookingConfig?.placementUid],
        queryFn: async () =>
            !!bookingConfig && schoolConfig.loaded
                ? await BOOKINGS_API.fetchTeacherFirstName({
                      ...bookingConfig,
                      ...schoolConfig.data,
                      dateObj: dayjs(bookingConfig.date, 'DD-MM-YYYY'),
                  })
                : null,
        staleTime: mToMs(10),
        gcTime: mToMs(30),
        enabled: !!bookingConfig && schoolConfig.loaded,
    })
}

export const useBookingType = ({
    placementUid,
    dateObj,
    interest,
}: {
    placementUid?: string
    dateObj: Dayjs
    interest: TeacherEmploymentSetting
}) => {
    const schoolConfig = useSelector(selectSchoolConfig)

    return useQuery<SchoolBookingType | null>({
        queryKey: ['bookings', placementUid, 'bookingType'],
        queryFn: async () => {
            try {
                if (!placementUid || !schoolConfig.loaded) {
                    throw new Error('Information missing!')
                }
                return await BOOKINGS_API.fetchType({
                    ...schoolConfig.data,
                    placementUid,
                    dateObj,
                    interest,
                })
            } catch (e) {
                return null
            }
        },
        enabled: !!placementUid && schoolConfig.loaded,
        staleTime: Infinity,
        gcTime: mToMs(30),
    })
}

export type CancelledBooking = SchoolBookingKeyEntity & {
    replacementState: BookingReplacementState | null
}

export type CombinedResult = {
    data: UseQueryResult[]
    uniqueTeachers: string[]
    pending: boolean
}

// Given school's placement keys, fetch the replacement status for each booking if cancelled
export const useBookingReplacementStates = ({
    dateObj,
    keys,
}: {
    dateObj: Dayjs
    keys: SchoolBookingKeyEntity[]
    replacing?: boolean
}) => {
    const schoolConfig = useSelector(selectSchoolConfig)
    return useQueries({
        queries: schoolConfig.loaded
            ? keys.map((key) => ({
                  queryKey: ['bookings', key.placementUid, 'replacementState'],
                  queryFn: async () => {
                      return {
                          ...key,
                          replacementState:
                              await BOOKINGS_API.fetchReplacementState({
                                  ...schoolConfig.data,
                                  dateObj,
                                  placementUid: key.placementUid,
                                  //   NOTE: Only for schools
                                  interest: 'school',
                              }),
                      } as CancelledBooking
                  },
                  staleTime: sToMs(30),
                  gcTime: mToMs(1),
              }))
            : [],
        combine: (results) => {
            return {
                data: results,
                pending: results.some((el) => el.isPending),
            }
        },
    })
}

export const useBookableForDate = (date: string) => {
    const config = useSelector(selectSchoolConfig)
    const query = useQuery<BookableTeachersForDate | null>({
        queryKey: ['bookableTeachers', config.data?.schoolId, date],
        queryFn: async () => {
            try {
                if (!config.loaded) throw new Error('invalid school config')
                logger.debug(`fetching bookables`)
                return fetchBookableTeacherForDate(
                    config.data.country,
                    config.data.locationState,
                    config.data.schoolId,
                    date
                )
            } catch (e) {
                return null
            }
        },
        enabled: config.loaded,
        staleTime: mToMs(1),
        gcTime: mToMs(5),
    })
    const count = useMemo(
        () =>
            query.data === undefined
                ? undefined
                : query.data === null
                  ? 0
                  : Object.keys(query.data).length,
        [query.data]
    )

    return { query, count }
}

export const useLoadDelay = (dep?: any, delay: number = 1000) => {
    const [hide, setHide] = useState(true)
    useEffect(() => {
        setHide(true)
        let timeout = setTimeout(() => setHide(false), delay)
        return () => {
            clearTimeout(timeout)
        }
    }, [dep, delay])
    return hide
}

export const useSkipper = () => {
    const shouldSkipRef = useRef(true)
    const shouldSkip = shouldSkipRef.current

    // Wrap a function with this to skip a pagination reset temporarily
    const skip = useCallback(() => {
        shouldSkipRef.current = false
    }, [])

    useEffect(() => {
        shouldSkipRef.current = true
    })

    return [shouldSkip, skip] as const
}

export const useTableTeachersData = (teacherUids: string[]) => {
    const config = useSelector(selectSchoolConfig)
    return useQueries({
        queries: teacherUids.map((uid) => ({
            queryKey: ['bookings', 'teacherData', uid],
            queryFn: async () =>
                config.loaded
                    ? fetchTeacherSimpleData(
                          config.data.country,
                          config.data.locationState,
                          uid
                      )
                    : null,
            staleTime: sToMs(3600), // 1 hour
            enabled: config.loaded,
        })),
        combine: (results) => ({
            data: results.reduce(
                (acc, curr, i) =>
                    !!curr.data ? { ...acc, [teacherUids[i]]: curr.data } : acc,
                {} as Record<string, TeacherSimpleData>
            ),
            pending: results.some((r) => r.isPending),
        }),
    })
}

export const useBookingAdditionalData = (keys: SchoolBookingKeyEntity[]) => {
    const config = useSelector(selectSchoolConfig)
    return useQueries({
        queries: keys.map((key) => {
            const dateObj = dayjs(key.date, 'YYYY-MM-DD')
            const interest: TeacherEmploymentSetting = !!key.esBooking
                ? 'supportStaff'
                : 'school'
            return {
                queryKey: ['bookings', 'tableData', key.placementUid],
                queryFn: async () =>
                    config.loaded
                        ? ({
                              id: key.placementUid,
                              bookingType: await BOOKINGS_API.fetchType({
                                  ...config.data,
                                  placementUid: key.placementUid,
                                  dateObj,
                                  interest,
                                  //   isSupportStaff: key.esBooking,
                              }),
                              bookingSubject:
                                  await BOOKINGS_API.fetchReplacingSubject({
                                      ...config.data,
                                      placementUid: key.placementUid,
                                      dateObj,
                                      interest,
                                  }),
                              bookingReplacing:
                                  (await BOOKINGS_API.fetchReplacingName({
                                      ...config.data,
                                      placementUid: key.placementUid,
                                      dateObj,
                                      interest,
                                  })) || '-',
                              bookingTerm: await BOOKINGS_API.fetchTerm({
                                  ...config.data,
                                  placementUid: key.placementUid,
                                  dateObj,
                                  interest,
                              }),
                              teacherVit: await BOOKINGS_API.fetchTeacherVIT({
                                  ...config.data,
                                  placementUid: key.placementUid,
                                  dateObj,
                                  interest,
                              }),
                          } as Omit<BookingTableData, 'bookingStatus'> & {
                              teacherVit: string | null
                          })
                        : null,
                staleTime: sToMs(3600), // 1 hours
                enabled: config.loaded || keys.length === 0,
            }
        }),
        combine: (results) => ({
            data: results.reduce(
                (acc, curr) =>
                    !!curr.data ? { ...acc, [curr.data.id]: curr.data } : acc,
                {} as Record<
                    string,
                    Omit<BookingTableData, 'bookingStatus'> & {
                        teacherVit: string | null
                    }
                >
            ),
            pending: results.some((r) => r.isPending),
        }),
    })
}

const log = true
type BookableTeacherAvailabilityForDate = BookableTeachersForDate[string]

type BookableStateUpdate =
    | {
          type: 'upsert'
          teacherUid: string
          availability: BookableTeacherAvailabilityForDate
      }
    | {
          type: 'remove'
          teacherUid: string
      }

const isSameAvailability = (
    a?: BookableTeacherAvailabilityForDate,
    b?: BookableTeacherAvailabilityForDate
) => a?.Primary === b?.Primary && a?.Secondary === b?.Secondary

const compareBooleanPriority = (a: boolean, b: boolean) => Number(b) - Number(a)

const compareDistancePriority = (a: number, b: number) => {
    const left = Number.isFinite(a) ? a : Number.POSITIVE_INFINITY
    const right = Number.isFinite(b) ? b : Number.POSITIVE_INFINITY

    return left - right
}

const compareTeacherNamePriority = (a: MoreTeacherRow, b: MoreTeacherRow) => {
    const left = `${a.teacherData?.firstName?.value || ''} ${
        a.teacherData?.lastName?.value || ''
    }`.trim()
    const right = `${b.teacherData?.firstName?.value || ''} ${
        b.teacherData?.lastName?.value || ''
    }`.trim()

    if (left !== right) return left.localeCompare(right)

    return a.teacherUid.localeCompare(b.teacherUid)
}

const sortBookableTeacherRows = (rows: MoreTeacherRow[]): MoreTeacherRow[] =>
    [...rows].sort((a, b) => {
        const bySchoolFavourite = compareBooleanPriority(
            a.teacherFavouritedBySchool,
            b.teacherFavouritedBySchool
        )
        if (bySchoolFavourite !== 0) return bySchoolFavourite

        const byTeacherFavourite = compareBooleanPriority(
            a.schoolFavouritedByTeacher,
            b.schoolFavouritedByTeacher
        )
        if (byTeacherFavourite !== 0) return byTeacherFavourite

        const byCompletedPlacement = compareBooleanPriority(
            a.teacherWorkedAtSchool,
            b.teacherWorkedAtSchool
        )
        if (byCompletedPlacement !== 0) return byCompletedPlacement

        const byDistance = compareDistancePriority(a.distance, b.distance)
        if (byDistance !== 0) return byDistance

        return compareTeacherNamePriority(a, b)
    })

export const useBookableTableDataForDate = (date: string) => {
    // Initial fetch from tanstack query to fill data
    const {
        query: { data: initialBookables, isPending },
    } = useBookableForDate(date)

    const [bookables, setBookables] = useState<BookableTeachersForDate | null>(
        null
    )
    const pendingUpdatesRef = useRef<BookableStateUpdate[]>([])
    const frameRef = useRef<number | null>(null)

    const schoolConfig = useSelector(selectSchoolConfig)

    const flushQueuedUpdates = useCallback(() => {
        frameRef.current = null

        if (pendingUpdatesRef.current.length === 0) return

        const queuedUpdates = pendingUpdatesRef.current
        pendingUpdatesRef.current = []

        setBookables((prev) => {
            let next = prev ?? {}
            let changed = false

            queuedUpdates.forEach((update) => {
                if (update.type === 'remove') {
                    if (!next[update.teacherUid]) return

                    if (!changed) next = { ...next }
                    delete next[update.teacherUid]
                    changed = true
                    return
                }

                if (
                    isSameAvailability(
                        next[update.teacherUid],
                        update.availability
                    )
                ) {
                    return
                }

                if (!changed) next = { ...next }
                next[update.teacherUid] = update.availability
                changed = true
            })

            return changed ? next : prev
        })
    }, [])

    const queueUpdate = useCallback(
        (update: BookableStateUpdate) => {
            pendingUpdatesRef.current.push(update)

            if (frameRef.current !== null) return

            frameRef.current = window.requestAnimationFrame(flushQueuedUpdates)
        },
        [flushQueuedUpdates]
    )

    useEffect(() => {
        if (initialBookables === undefined) return
        setBookables(initialBookables)
    }, [initialBookables])

    // TODO: Make this live
    useEffect(() => {
        if (!schoolConfig.loaded || !schoolConfig.data) return

        logger.debug(
            `ATTACH-LISTENER: bookables ${date} ${schoolConfig.data.schoolId}`
        )

        let unsubs = SCHOOLS_API.bookables.listenForDate(
            {
                ...schoolConfig.data,
                queryType: 'date',
                interest: 'school',
                schoolUid: schoolConfig.data.schoolId,
            },
            date,
            {
                added: (snap) => {
                    queueUpdate({
                        type: 'upsert',
                        teacherUid: snap.key || '',
                        availability: snap.val(),
                    })
                },
                changed: (snap) => {
                    queueUpdate({
                        type: 'upsert',
                        teacherUid: snap.key || '',
                        availability: snap.val(),
                    })
                },
                removed: (snap) => {
                    queueUpdate({
                        type: 'remove',
                        teacherUid: snap.key || '',
                    })
                },
            }
        )

        return () => {
            const turnOff = () => {
                if (frameRef.current !== null) {
                    window.cancelAnimationFrame(frameRef.current)
                    frameRef.current = null
                }
                pendingUpdatesRef.current = []
                logger.debug(
                    `DETACH-LISTENER: bookables ${date} ${schoolConfig.data.schoolId}`
                )
                unsubs.forEach((offFn) => offFn())
            }

            if ('requestIdleCallback' in window) {
                window.requestIdleCallback(turnOff)
            } else {
                setTimeout(turnOff, 0)
            }
        }
    }, [schoolConfig.loaded, schoolConfig.data, date, queueUpdate])

    const uniqueTeachers = useMemo(
        () => Object.keys(bookables || {}),
        [bookables]
    )

    const teachersData = useTeacherProfiles({
        teacherUids: uniqueTeachers,
    })

    const schoolFavourites = useSelector(selectSchoolFavourites, JSONEqual)
    const schoolCoords = useSelector(selectSchoolCoordinates, JSONEqual)

    return useMemo(():
        | { pending: true; data: undefined }
        | { pending: false; data: MoreTeacherRow[] } => {
        if (!schoolConfig.loaded || teachersData.pending || isPending)
            return {
                pending: true,
                data: undefined,
            }

        const schoolUid = schoolConfig.data.schoolId
        const rows = Object.entries(bookables || {}).map(
            ([uid, av]): MoreTeacherRow => {
                const teacherData = teachersData.data[uid]
                const teacherLocation = teacherData?.setLocation
                const hasTeacherLocation =
                    typeof teacherLocation?.lat === 'number' &&
                    typeof teacherLocation?.long === 'number'

                return {
                    teacherUid: uid,
                    availability: av,
                    teacherData,

                    teacherFavouritedBySchool: !!schoolFavourites?.[uid],
                    schoolFavouritedByTeacher: Boolean(
                        teacherData?.favouriteSchools?.[schoolUid]
                    ),

                    teacherWorkedAtSchool: Boolean(
                        teacherData?.completedSchools?.[schoolUid]
                    ),
                    distance: hasTeacherLocation
                        ? distanceBetween(schoolCoords, [
                              teacherLocation.lat || 0,
                              teacherLocation.long || 0,
                          ])
                        : Number.POSITIVE_INFINITY,
                }
            }
        )

        return {
            pending: false,
            data: sortBookableTeacherRows(rows),
        }
    }, [
        bookables,
        isPending,
        teachersData.data,
        teachersData.pending,
        schoolConfig.loaded,
        schoolConfig.data.schoolId,
        schoolFavourites,
        schoolCoords,
    ])
}

export const useSchoolTeacherFavourite = (teacherUid: string) => {
    const schoolUid = useSelector(selectSchoolUid)
    const { data: isFavourited } = useQuery({
        queryKey: ['schoolTeacherFavourite', schoolUid, teacherUid],
        queryFn: async () => {
            if (!schoolUid) return false
            return await SCHOOLS_API.teachers.fetchIsFavourite({
                teacherUid,
                schoolUid,
            })
        },
        enabled: !!schoolUid,
        staleTime: mToMs(5),
        gcTime: mToMs(10),
    })

    const queryClient = useQueryClient()
    const { mutateAsync, isPending, variables } = useMutation({
        mutationFn: async (isFavourited: boolean) => {
            if (!schoolUid)
                throw new Error(
                    'Not authenticated. Please refresh and try again.'
                )
            await SCHOOLS_API.teachers.setFavourite(
                teacherUid,
                schoolUid,
                isFavourited
            )
            return [teacherUid, schoolUid, isFavourited] as [
                string,
                string,
                boolean,
            ]
        },
        onSuccess: (args: [string, string, boolean]) => {
            queryClient.setQueryData(
                ['schoolTeacherFavourite', args[1], args[0]],
                args[2]
            )
            // queryClient.invalidateQueries({
            //     queryKey: ['schoolTeacherFavourite', args[1], args[0]],
            // })
        },
    })

    const handleChange = useCallback(
        (e: boolean) => mutateAsync(e),
        [mutateAsync]
    )

    return [isPending ? variables : isFavourited, handleChange] as [
        boolean,
        (e: boolean) => Promise<any>,
    ]
}

export const useTeacherPublicInfoField = <T extends string | null>(
    teacherUid: string,
    fieldName: keyof TeacherPublicInfo
) => {
    const schoolConfig = useSelector(selectSchoolConfig)

    return useQuery({
        queryKey: [`teacherPublicInfo`, fieldName, teacherUid],
        queryFn: () => {
            if (!schoolConfig.loaded) return null
            return SCHOOLS_API.teachers.get<T>(
                { ...schoolConfig.data, teacherUid },
                fieldName
            )
        },
        enabled: schoolConfig.loaded,
        staleTime: Infinity,
    })
}

export const useTeachersProfilePictures = (uids: string[]) => {
    const schoolConfig = useSelector(selectSchoolConfig)

    return useQueries({
        queries: uids.map((uid) => {
            return {
                queryKey: ['teacherProfilePicture', uid],
                queryFn: async () => {
                    if (!schoolConfig.loaded) return null
                    await new Promise<void>((res) => setTimeout(res, 1000))
                    return SCHOOLS_API.teachers.get<string>(
                        { ...schoolConfig.data, teacherUid: uid },
                        'profilePicture'
                    )
                },
            }
        }),
        combine: (results) => ({
            data: results.map((r) => r.data),
            pending: results.some((r) => r.isPending),
        }),
    })
}

export const useBookingNotes = (
    booking: { id: string; date: string },
    interest: TeacherEmploymentSetting
) => {
    const schoolConfig = useSelector(selectSchoolConfig)

    return useQuery({
        queryKey: ['bookings', 'schoolNotes', booking.id],
        queryFn: async () => {
            if (!schoolConfig.loaded) return null
            return {
                key: 'schoolNotes',
                data: await BOOKINGS_API.fetchChild<BookingSchoolNotes>(
                    {
                        ...schoolConfig.data,
                        placementUid: booking.id,
                        dateObj: dayjs.tz(
                            booking.date,
                            'DD-MM-YYYY',
                            'Australia/Melbourne'
                        ),
                        interest,
                    },
                    'schoolNotes'
                ),
            }
        },
    })
}

export const useTeacherEmploymentSetting = (
    location: LocationParams,
    teacherUid: string
) =>
    useQuery({
        queryKey: [`teachers`, 'employmentSettings', teacherUid],
        queryFn: async () =>
            SCHOOLS_API.teachers.getEmploymentSetting({
                ...location,
                teacherUid,
            }),
        staleTime: 10000,
        gcTime: 10000,
    })
